<template>
  <div class="vch-contact-form">
    <h2 class="vch-contact-form__title heading-xl uppercase mb-xl">
      {{ currentTitle }}
    </h2>
    <template v-if="!formSuccess">
      <div class="vch-grid">
        <div
          class="
            vch-col-12
            vch-col-md-10
            vch-col-offset-md-1
            vch-col-lg-8
            vch-col-offset-lg-2
            vch-col-xl-6
            vch-col-offset-xl-3
          "
        >
          <p class="vch-contact-form__instructions">
            {{ formMandatoryInstruction }}
          </p>
          <slot name="form">Form Here</slot>
        </div>
      </div>
    </template>
    <template v-else>
      <h2 class="heading-lg uppercase mb-xxs">
        <span class="heading-lg-em">{{ completeTitlePrimary }}</span
        ><br />{{ completeTitleSecondary }}
      </h2>
      <p class="heading-md mb-xl" v-html="completeMessage"></p>
      <a
        href="/"
        class="button -primary --fit-content"
        @click="pushReturnTag"
        >{{ confirmButtonText }}</a
      >
    </template>
  </div>
</template>

<script>
import { parseSitecoreForm, bindFormField } from 'Utils/form';
import { pushEvent } from 'Utils/datalayer';
import { mixinFormRecaptcha } from 'Mixins/formRecaptcha/formRecaptcha';

export default {
  name: 'vch-contact-form',
  mixins: [mixinFormRecaptcha],
  props: {
    formTitle: {
      type: String,
      required: true,
    },
    formMandatoryInstruction: {
      type: String,
    },
    confirmTitlePrimary: {
      type: String,
      required: true,
    },
    confirmTitleSecondary: {
      type: String,
      required: true,
    },
    confirmMessage: {
      type: String,
      required: true,
    },
    confirmTitleNewsletterPrimary: {
      type: String,
      required: true,
    },
    confirmTitleNewsletterSecondary: {
      type: String,
      required: true,
    },
    confirmNewsletterMessage: {
      type: String,
      required: true,
    },
    confirmButtonText: {
      type: String,
      required: true,
    },
    errorMessageFormIncomplete: {
      type: String,
      required: true,
    },
    errorMessageFormTerms: {
      type: String,
      required: true,
    },
    defaultErrorMessage: {
      type: String,
      required: false,
      default: 'An error occured while processing your request.',
    },
    taggingOrigin: {
      type: String,
      required: false,
      default: 'contact form',
    },
    testHost: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      currentTitle: this.formTitle,
      formSuccess: false,
      useEmailFlow: false,
      fields: [],
      formProperties: null,
      parser: new DOMParser(),
      errorWrapperNode: null,
      eventTriggered: false,
    };
  },
  computed: {
    completeTitlePrimary() {
      return this.useEmailFlow
        ? this.confirmTitleNewsletterPrimary
        : this.confirmTitlePrimary;
    },
    completeTitleSecondary() {
      return this.useEmailFlow
        ? this.confirmTitleNewsletterSecondary
        : this.confirmTitleSecondary;
    },
    completeMessage() {
      return this.useEmailFlow
        ? this.confirmNewsletterMessage
        : this.confirmMessage;
    },
    formActionUrl() {
      return process.env.NODE_ENV === 'production'
        ? this.formProperties.formAction
        : `${this.testHost}${this.formProperties.formAction}`;
    },
  },
  mounted() {
    // To add custome styled toggle button
    if (document.documentElement.lang === 'th-TH') {
      this.togglerCreator();
    }

    const formNode = this.$slots.form[0].children[0].children[0];
    if (!formNode?.elm) {
      return;
    }

    // Collect the base form properties and hijack form submit
    this.formProperties = parseSitecoreForm(formNode?.elm);
    formNode.elm.addEventListener('submit', this.onSubmit);

    // Create the error message wrapper
    this.errorWrapperNode = document.createElement('div');
    this.errorWrapperNode.setAttribute('role', 'status');
    this.errorWrapperNode.classList.add('vch-contact-form__errorSummary');
    const submitButton = formNode?.elm.querySelector('input[type="submit"]');
    submitButton.parentNode.insertBefore(this.errorWrapperNode, submitButton);

    // Traverse the form and collect all the inputs into a single list
    formNode.children
      .filter((node) => node.tag === 'div' || node.tag === 'input')
      .forEach((node) => {
        const foundFieldInfo = bindFormField(node);
        if (foundFieldInfo.nestedFieldInfo.length) {
          this.fields.push(...foundFieldInfo.nestedFieldInfo);
        }
        this.fields.push(foundFieldInfo);
      });
    this.setupNewsletterToggle();
  },
  methods: {
      async onSubmit(e) {
      e.preventDefault();
      if (!this.validateFields()) return;

      let submitButton = document.querySelector('.vch-form-submit');
      // Recaptcha Flow via Mixin
      this.peformRecaptcha()
        .then(async (canSubmit) => {
          if (canSubmit) {
            if (submitButton) {
              submitButton.setAttribute('disabled', 'disabled');
            }
            await fetch(this.formActionUrl, {
              method: 'POST',
              mode: process.env.NODE_ENV === 'production' ? 'cors' : 'no-cors',
              body: this.assembleFormData(),
            })
              .then((response) => {
                if (response.status >= 400 && response.status < 600) {
                  throw new Error(this.defaultErrorMessage);
                }
                return response;
              })
              .then((response) => {
                const htmlResponse = this.parser.parseFromString(
                  response,
                  'text/xml'
                );
                const errorWrapper = htmlResponse.querySelector(
                  '.validation-summary-errors'
                );
                if (errorWrapper) {
                  const serverErrorMsgs = errorWrapper.querySelectorAll('li');
                  const summaryErrors = serverErrorMsgs.map(
                    (msg) => msg.textContent
                  );
                  this.setErrorMessage(summaryErrors);
                  if (submitButton) {
                    submitButton.removeAttribute('disabled');
                  }
                } else {
                  this.formSuccess = true;
                  this.pushSubmitTags();
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                  });
                }
              })
              .catch((error) => {
                this.setErrorMessage(error.message);
                if (submitButton) {
                  submitButton.removeAttribute('disabled');
                }
              });
          }
        })
        .catch(() => {
          this.setErrorMessage(this.defaultErrorMessage);
          if (submitButton) {
            submitButton.setAttribute('disabled', 'disabled');
          }
        });
    },
    setErrorMessage(errorMessage) {
      this.errorWrapperNode.innerText = errorMessage;
    },
    setupNewsletterToggle() {
      const newsletterCheckbox = this.fields.find(
        (field) => field.isNewsletterSignup
      );
      newsletterCheckbox.inputElement.addEventListener('change', () => {
        this.useEmailFlow = newsletterCheckbox.inputElement.checked;
      });
    },
    togglerCreator() {
      let togglerItem = document.createElement('div');
      togglerItem.className = 'toggler-item';
      let togglerDivs = document.querySelectorAll('.salutation_container');
      togglerDivs.forEach((togglerDivs) => {
        let togglerOuter = togglerDivs.getElementsByTagName('label');
        for (let i = 1; i < togglerOuter.length; i++) {
          togglerOuter[i].appendChild(togglerItem.cloneNode(true));
          togglerOuter[i].classList.add('toggler-item-label');
        }
      });
    },
    validateFields() {
      const validationErrors = [];
      let hasTermsError = false;
      this.fields.forEach((field) => {
        const fieldErrors = field.validate();
        if (fieldErrors.length && field.isTermsAgree) {
          hasTermsError = true;
        }
        validationErrors.push(...fieldErrors);
      });

      if (hasTermsError) {
        this.setErrorMessage(this.errorMessageFormTerms);
        return false;
      } else if (validationErrors.length > 0) {
        this.setErrorMessage(this.errorMessageFormIncomplete);
        return false;
      }
      return true;
    },
    assembleFormData() {
      const formData = new FormData();
      this.fields.forEach((field) => {
        if (field.inputElement) {
          formData.append(field.inputElement.name, field.inputElement.value);
        }
        field.hiddenFields.forEach((hiddenField) => {
          if (hiddenField.name) {
            formData.append(hiddenField.name, hiddenField.value);
          }
        });
      });
      return formData;
    },
    pushSubmitTags() {
      if (!this.eventTriggered) {
        if (this.useEmailFlow) {
          pushEvent({
            event: 'uaevent',
            event_name: 'newsletter_registration',
            ecommerce: 'undefined',
            eventCategory: 'registration',
            eventAction: 'newsletter',
            eventLabel: this.taggingOrigin,
            referral: this.taggingOrigin,
          });
        }

        pushEvent({
          event: 'uaevent',
          ecommerce: 'undefined',
          eventCategory: 'form',
          eventAction: 'submit',
          eventLabel: 'contact form',
          event_name: 'submit_contact_form',
          form_name: 'contact form',
        });
        this.eventTriggered = true;
      }
    },
    pushReturnTag() {
      pushEvent({
        event: 'uaevent',
        ecommerce: 'undefined',
        eventCategory: 'appendix',
        eventAction: 'select::vch-contact-form',
        eventLabel: `${this.confirmButtonText.toLowerCase()}::${
          window.location.origin
        }`,
        event_name: 'body_button_click',
        cta_name: `${this.confirmButtonText.toLowerCase()}`,
        link_url: `${window.location.origin}`,
        module_name: 'vch-contact-form',
      });
    },
  },
};
</script>

<style lang="scss" src="./contact-form.scss"></style>
